import React, { useState, useEffect, useCallback, memo } from 'react';

import { GridListTile } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import NoDataComponent from 'components/NoDataComponent';
import { useSelector, shallowEqual } from 'react-redux';
import { StyledGridList } from 'screens/MeshMoney/Vouchers';
import TasksListV1 from 'screens/home/tasksList/TasksListV1';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';

import { TASK_TYPES_2 } from 'constants/ProjectConstants';

import { getTeamTasksAndStatsForCardViewNew } from 'services/task';

const emptyStats = {
  delayed: 0,
  ongoing: 0,
  unplanned: 0,
  completed: 0,
  highProgress: 0,
  mediumProgress: 0,
  lowProgress: 0,
  onTrackProgress: 0,
  offTrackProgress: 0,
  atRiskProgress: 0,
  closedProgress: 0,
};

function TasksListContainer({ employee, stats, type, timeBoundPeriod = null, selectedCohort = null }) {
  return (
    <StyledTasksList
      stats={stats}
      employee={employee}
      type={type}
      source="teamPage"
      timeBoundPeriod={timeBoundPeriod}
      selectedCohort={selectedCohort}
    />
  );
}

function TeamTasksCardView({
  tagId,
  durationKey,
  setTeamCount,
  style,
  isSectionVisible,
  activeFilters,
  updateTasksLoading,
  updateIsTasksDataChanged,
  hasTableViewDataChanged,
  refreshData,
  tagType,
  selectedTeam, // either team selected in the header dropdown or the team on team page
  type,
  showListView,
  onEmployeeListUpdate,
  startTS,
  endTS,
  // only in case of goals
  timeBoundPeriod = null,
  selectedCohort = null,
}) {
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState({ team: {}, tasks: {}, stats: {} });
  const limit = 5;
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const loadData = useCallback(() => {
    if (!tagId || !type) return;
    setLoading(true);
    updateTasksLoading(true);
    getTeamTasksAndStatsForCardViewNew({
      durationKey,
      limit,
      filters: activeFilters,
      tagType,
      tagId,
      type,
      startTS,
      endTS,
      timeBoundPeriod,
      selectedCohort,
    })
      .then((teamData) => {
        setTeamData(teamData);
        const teamDataIds = Object.keys(teamData.team);
        const teamMemberIds = teamDataIds.filter((id) => teamData.team[id].userRole !== 'UNASSIGNED_USER');
        const teamDataCount = teamMemberIds.length;
        setTeamCount(teamDataCount);

        if (onEmployeeListUpdate && teamData && teamData.teamOrder) {
          onEmployeeListUpdate(teamData.teamOrder);
        }
      })
      .catch((err) => {
        setTeamData({ team: {}, tasks: {}, stats: {} });
        setTeamCount(0);
      })
      .finally(() => {
        updateTasksLoading(false);
        setLoading(false);
      });
  }, [
    JSON.stringify(activeFilters),
    durationKey,
    type,
    setTeamCount,
    tagId,
    tagType,
    updateTasksLoading,
    startTS,
    endTS,
  ]);

  useEffect(() => {
    if (!tagId || !tagType || !type) return;
    loadData();
  }, [tagId, tagType, durationKey, type, JSON.stringify(activeFilters), startTS, endTS]);

  useEffect(() => {
    if (!tagId || !tagType || !type) return;
    if (!refreshData) return;
    loadData();
  }, [refreshData]);

  useEffect(() => {
    if (isSectionVisible && hasTableViewDataChanged) {
      loadData();
      updateIsTasksDataChanged(false);
    }
  }, [hasTableViewDataChanged, isSectionVisible]);

  const employeeIds = teamData.teamOrder || [];

  const noResultsMessage = (type = 'TASK') => `No ${type.underscoreLess().toLowerCase()}s found!`;

  return isSectionVisible ? (
    <>
      {loading ? (
        <Skeleton height="40vh" style={style} />
      ) : employeeIds.length > 0 ? (
        <MyStyledGridList id="horizonalCardView" spacing={24} cols={3.4} style={style}>
          {employeeIds.map((employeeId, index) => (
            /* const activeTasks = teamData.tasks[employeeId]
              ? teamData.tasks[employeeId]['ACTIVE'] || []
              : [];
            const completedTasks = teamData.tasks[employeeId]
              ? teamData.tasks[employeeId]['COMPLETED'] || []
              : []; */
            <StyledGridListTile className={`onboardingTeamhubMyOKRs-${index}`}>
              <TasksListContainer
                stats={teamData.stats[employeeId] || emptyStats}
                employee={teamData.team[employeeId]}
                selectedTeam={selectedTeam}
                selectedCohort={selectedCohort}
                timeBoundPeriod={timeBoundPeriod}
                type={type}
              />
            </StyledGridListTile>
          ))}
        </MyStyledGridList>
      ) : (
        <StyledNoDataComponent
          height="13vh"
          message={type === TASK_TYPES_2.goal ? 'No Data to display' : noResultsMessage(type)}
          style={style}
        />
      )}
    </>
  ) : null;
}

export default memo(TeamTasksCardView);

const MyStyledGridList = styled(StyledGridList)`
  height: 88vh;
  li:first-child {
    padding-left: 0 !important;
  }
  transform: none;
  margin-bottom: 1rem !important;
`;

const StyledGridListTile = styled(GridListTile)`
  // height: 50vh !important;
  height: 100% !important;
`;

const StyledTasksList = styled(TasksListV1)`
  & > .tasksContainer {
    height: 84%;
  }
`;

const StyledNoDataComponent = styled(NoDataComponent)`
  font-size: ${(props) => props.theme.typography.h6.fontSize};
`;
